// Utility function to generate curved polyline coordinates using quadratic bezier curves

export interface LatLng {
    lat: number;
    lng: number;
}

/**
 * Generates a curved path between two points using quadratic Bezier curve
 * @param start Starting point
 * @param end Ending point
 * @param curvature Amount of curve (default: 0.5)
 * @param segments Number of segments for smoothness (default: 50)
 * @returns Array of LatLng points forming a curve
 */
export const generateCurvedPath = (
    start: LatLng,
    end: LatLng,
    curvature: number = 0.5,
    segments: number = 50
): LatLng[] => {
    const curvePoints: LatLng[] = [];

    // Calculate distance between points (rough approximation)
    const latDiff = Math.abs(end.lat - start.lat);
    const lngDiff = Math.abs(end.lng - start.lng);
    const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

    // Scale curvature proportionally to distance so nearby points
    // get nearly straight lines instead of large looping arcs.
    // The offset is clamped: minimal for close points, full curvature beyond 2 degrees.
    const scaleFactor = Math.min(distance / 2, 1);
    let adjustedCurvature = curvature * scaleFactor;

    // Calculate midpoint for the curve control point
    const midLat = (start.lat + end.lat) / 2;
    const midLng = (start.lng + end.lng) / 2;

    // Offset the control point perpendicular to the line for curve effect
    const angle = Math.atan2(end.lat - start.lat, end.lng - start.lng);
    const offset = adjustedCurvature;
    const controlLat = midLat + offset * Math.cos(angle);
    const controlLng = midLng - offset * Math.sin(angle);

    // Generate points along the quadratic bezier curve
    for (let t = 0; t <= segments; t++) {
        const ratio = t / segments;
        const lat =
            Math.pow(1 - ratio, 2) * start.lat +
            2 * (1 - ratio) * ratio * controlLat +
            Math.pow(ratio, 2) * end.lat;
        const lng =
            Math.pow(1 - ratio, 2) * start.lng +
            2 * (1 - ratio) * ratio * controlLng +
            Math.pow(ratio, 2) * end.lng;
        curvePoints.push({ lat, lng });
    }

    return curvePoints;
};

/**
 * Calculates bearing angle between two points (for arrow direction)
 */
export const calculateBearing = (start: LatLng, end: LatLng): number => {
    const dLon = end.lng - start.lng;
    const y = Math.sin(dLon) * Math.cos(end.lat);
    const x =
        Math.cos(start.lat) * Math.sin(end.lat) -
        Math.sin(start.lat) * Math.cos(end.lat) * Math.cos(dLon);
    const bearing = Math.atan2(y, x);
    return (bearing * 180) / Math.PI;
};
